// Thin wrapper over the browser's Fullscreen API. The settings UI and the
// language-screen Continue handler both go through this so prefixed
// implementations (Safari's webkit*) and the always-async return shape
// stay in one place.
//
// Note: requestFullscreen() requires a user activation (click / keydown).
// Gamepad input is not a user activation on most browsers, so the call
// can reject; callers swallow the error and the UI keeps working.

interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}

interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

export function isFullscreen(): boolean {
  const d = document as FullscreenDocument;
  return Boolean(d.fullscreenElement ?? d.webkitFullscreenElement);
}

export async function enterFullscreen(): Promise<void> {
  if (isFullscreen()) return;
  const el = document.documentElement as FullscreenElement;
  try {
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
  } catch {
    // Permissions / user-activation failures are non-fatal — the UI still
    // works in windowed mode.
  }
}

export async function exitFullscreen(): Promise<void> {
  if (!isFullscreen()) return;
  const d = document as FullscreenDocument;
  try {
    if (d.exitFullscreen) await d.exitFullscreen();
    else if (d.webkitExitFullscreen) await d.webkitExitFullscreen();
  } catch {
    // ignore
  }
}

/** Subscribe to fullscreen-state changes (entered or exited). Returns a
 *  detach function. Listens to both the standard event and the webkit
 *  prefixed variant so Safari users get live updates too. */
export function onFullscreenChange(cb: () => void): () => void {
  document.addEventListener('fullscreenchange', cb);
  document.addEventListener('webkitfullscreenchange', cb);
  return () => {
    document.removeEventListener('fullscreenchange', cb);
    document.removeEventListener('webkitfullscreenchange', cb);
  };
}
